import {css, useTheme} from '@mui/material';
import * as React from 'react';
import {Modal} from 'react-bootstrap';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import styles from './ConfirmDialog.less';
import {DlMuiButton} from './DlMuiButton';
import {GetStylesFunction} from '../common';
import {TranslateUtils} from 'src/js/common/utils';

const I18N = defineMessages({
  ACCEPT: {
    id: 'common.confirmDialog.accept',
    description: 'accept text for confirm button in confirm dialog',
  },
  CANCEL: {
    id: 'common.confirmDialog.cancel',
    description: 'text for cancel button in confirm dialog',
  },
  CONFIRM: {
    id: 'common.confirmDialog.confirm',
    description: 'confirm text for confirm button in confirm dialog',
  },
  TITLE: {
    id: 'common.confirmDialog.title',
    description: 'title text for confirm dialog',
  },
});

interface OwnProps {
  cancelText?: string;
  confirmText?: string;
  handleCancel: () => void;
  handleConfirm: () => void;
  titleIcon?: JSX.Element;
  titleText?: string;
  isOpened: boolean;
  showCancel?: boolean;
}

type Props = OwnProps;

export const ConfirmDialog: React.FC<Props> = (props) => {
  const {
    cancelText = I18N.CANCEL.id,
    children,
    confirmText = I18N.CONFIRM.id,
    handleCancel,
    handleConfirm,
    isOpened,
    showCancel = true,
    titleIcon,
    titleText = I18N.TITLE.id,
  } = props;

  const intl = useIntl();

  const cancelIntlProps = {...I18N.CANCEL, id: cancelText};
  const confirmIntlProps = {
    ...(showCancel ? I18N.CONFIRM : I18N.ACCEPT),
    ...(confirmText ? {id: confirmText} : {}),
  };
  const titleIntlProps = {...I18N.TITLE, id: titleText};

  const theme = useTheme();
  const muiStyles = getStyles({theme});
  return (
    <Modal show={isOpened} onHide={handleCancel} css={muiStyles.modal}>
      <Modal.Header closeButton={false} css={muiStyles.header}>
        <Modal.Title css={muiStyles.headerTitle}>
          {titleIcon}
          <FormattedMessage {...titleIntlProps} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.dlPreserveWhitespaces} data-name={'confirmDialogBody'}>
          {children}
        </div>
      </Modal.Body>
      <Modal.Footer css={muiStyles.footer}>
        {showCancel ? (
          <DlMuiButton
            data-name='modal-cancel-button'
            type='chip-gray'
            big
            title={TranslateUtils.translate(cancelIntlProps, intl)}
            onClick={handleCancel}
          />
        ) : (
          <div />
        )}
        <DlMuiButton
          data-name='modal-confirm-button'
          type='chip2-filled'
          big
          title={TranslateUtils.translate(confirmIntlProps, intl)}
          onClick={handleConfirm}
        />
      </Modal.Footer>
    </Modal>
  );
};

ConfirmDialog.displayName = 'ConfirmDialog';

const getStyles: GetStylesFunction = (options) => {
  return {
    modal: css`
      border-radius: 16px;
    `,
    header: css`
      border-bottom: none;
    `,
    headerTitle: css`
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 12px;
    `,
    footer: css`
      border-top: none;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 8px;
      & > * {
        flex: 1;
      }
      & > div:nth-child(2) {
        display: flex;
        justify-content: flex-end;
      }
    `,
  };
};
